import { ReactNode } from 'react'
import Image from 'next/image'
import { Button, IconPlayDefault } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'

// mccerts-2548_v1-subscriptions-entry-point: variant hero layout. Mirror of the
// shared HeroLayout (components/common/HeroLayout) with the variant treatment
// baked in — a 50/50 split (trailer ~half the page), top-aligned columns and a
// tighter gap. No media maxWidth: the page `.container` caps total width.
interface HeroLayoutProps {
  children: ReactNode
  imageUrl?: string
  imageAlt: string
  showTrailerButton?: boolean
  onTrailerClick?: () => void
  trailerMediaUuid?: string
  className?: string
  // Mirrors the shared HeroLayout's `media` slot so mccerts-2279's hero trailer
  // loop renders on the variant heroes too — that experiment has to be
  // independent of this one's bucket. Carry this over to the winning hero at
  // productization.
  media?: ReactNode
}

export const HeroLayout = ({
  children,
  imageUrl,
  imageAlt,
  showTrailerButton = false,
  onTrailerClick,
  trailerMediaUuid,
  className,
  media,
}: HeroLayoutProps) => {
  const { t } = useTranslation('@mc/certificates')

  return (
    <div className={className}>
      <div className='container flex flex-col lg:flex-row items-center lg:items-start justify-between gap-8 my-7 md:my-8'>
        <div className='flex flex-col items-center lg:items-start gap-6 flex-1'>
          {children}
        </div>

        {imageUrl && (
          <div className='relative w-full lg:mt-5 flex-1 aspect-square'>
            <Image
              className='rounded-xl object-cover'
              src={imageUrl}
              alt={imageAlt}
              fill
              sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 615px'
              priority
            />
            {media}
            {showTrailerButton && trailerMediaUuid && (
              <Button
                kind='secondary'
                className='absolute bottom-0 left-0 m-5 py-2 px-4 rounded-lg'
                onClick={onTrailerClick}
              >
                <div className='flex justify-center items-center bg-c-base-1400 text-c-text-light rounded-full p-1 mr-2'>
                  <IconPlayDefault size='sm' />
                </div>
                {t('marketingPages.common.playTrailer')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
